import { unresolvedAnchor } from '../../../shared/routeAnchor';
import type {
  AreaStatus,
  IsoDate,
  IsoDateTime,
  RouteStop,
  ScanEstimate,
  StoredRoute,
  Suggestion,
} from '../../../shared/types';
import { scanStateLabel } from '../ui/labels';
import { addDays, formatDate, pacificDateTime, parseIsoDate } from './dates';

/** Pure helpers for Plan tomorrow (D83). */

const WEEKDAYS_LONG = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/** The next day after `today` that is a workday (Sunday 0); Friday, Saturday and Sunday plan Monday. */
export const nextWorkday = (today: IsoDate, workdays: readonly number[]): IsoDate => {
  for (let i = 1; i <= 7; i++) {
    const day = addDays(today, i);
    if (workdays.includes(parseIsoDate(day).getDay())) return day;
  }
  return addDays(today, 1);
};

/** "Tuesday, Oct 20". */
export const dayTitle = (day: IsoDate): string =>
  `${WEEKDAYS_LONG[parseIsoDate(day).getDay()]}, ${formatDate(day, day)}`;

/** "14:00" → "2:00 PM". */
export const clockText = (hhmm: string): string => {
  const [h = 0, m = 0] = hhmm.split(':').map(Number);
  const hour = h % 12 === 0 ? 12 : h % 12;
  return `${hour}:${String(m).padStart(2, '0')} ${h % 24 < 12 ? 'AM' : 'PM'}`;
};

const driveText = (minutes: number): string => {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h === 0 ? `${m} min` : m === 0 ? `${h} h` : `${h} h ${m} min`;
};

/** "18 stops · 1 h 40 min driving${waiting ? ` · ${driveText(waiting)} waiting` : ''} · done by 5:55 PM". */
export const totalsText = (s: Pick<Suggestion, 'totals' | 'gaps'>): string => {
  const { stops, drive_min, ends } = s.totals;
  const waiting = (s.gaps ?? []).reduce((n, g) => n + g.minutes, 0);
  return `${stops} ${stops === 1 ? 'stop' : 'stops'} · ${driveText(drive_min)} driving${waiting ? ` · ${driveText(waiting)} waiting` : ''} · done by ${clockText(ends)}`;
};

/** The accepted suggestion as its day's route: the planner's order and arrival times kept. */
export const suggestionToRoute = (
  s: Suggestion,
  _home: unknown,
  now: IsoDateTime,
): StoredRoute => ({
  anchor: unresolvedAnchor('settings_unproven'),
  stops: s.stops.map(({ leave: _l, drive_min: _d, late: _late, ...stop }): RouteStop => stop),
  built: true,
  planned: true,
  area: s.area,
  day: s.day,
  created_at: now,
});

/** What the settings form sends (the API's `settingsBody` fields, minus the busy-hours switch). */
export interface SettingsDraft {
  home_label: string;
  start_time: string;
  day_minutes: number;
  stop_minutes: number;
  meeting_minutes: number;
  target_stops: number;
  workdays: readonly number[];
}

const between = (n: number, lo: number, hi: number) => n >= lo && n <= hi;

/** The API's ranges (api/src/routes/daily.ts `settingsBody`), checked here first so the toast names the
 *  field in the rep's words instead of the server's bare "Invalid request" (D44). */
export const settingsProblem = (d: SettingsDraft): { field: keyof SettingsDraft; text: string } | null => {
  const home = d.home_label.trim();
  if (home.length < 3) return { field: 'home_label', text: 'Home address needs at least 3 characters' };
  if (home.length > 200)
    return { field: 'home_label', text: 'Home address is too long (200 characters at most)' };
  if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(d.start_time))
    return { field: 'start_time', text: 'Pick a start time' };
  if (!between(d.day_minutes, 60, 900))
    return { field: 'day_minutes', text: 'Day length must be 1–15 hours' };
  if (!between(d.stop_minutes, 5, 600))
    return { field: 'stop_minutes', text: 'Minutes per stop must be 5–600' };
  if (!between(d.meeting_minutes, 5, 600))
    return { field: 'meeting_minutes', text: 'Minutes per meeting must be 5–600' };
  if (!between(d.target_stops, 1, 40)) return { field: 'target_stops', text: 'Stops a day must be 1–40' };
  if (d.workdays.length === 0) return { field: 'workdays', text: 'Pick at least one workday.' };
  return null;
};

/** The question asked before an area's first Google look-up (D82): nothing is spent without a yes. */
export const scanAsk = (est: ScanEstimate): string => {
  if (est.state === 'complete' || est.state === 'legacy-complete') return `${est.area}: Complete; no calls are needed. Recorded area usage: ${est.recorded_calls ?? 'unavailable'} lookups; ${est.reserved_calls ?? 0} unresolved reserved lookups.`;
  if (est.state === 'running') return `A lookup of ${est.area} is running; wait for it to finish.`;
  if (est.cap != null && (est.month_calls ?? 0) >= est.cap) return `No lookups left this month (${est.month_calls ?? 0} of ${est.cap}).`;
  const action = est.state === 'legacy-unknown' ? 'Progress unknown; starting over re-walks from the beginning.'
    : est.state === 'stale' ? 'Start a new generation; previous coverage is stale and this re-walks from the beginning, including any unresolved calls.'
    : est.state === 'interrupted' ? 'Recover interrupted scan; uncertain calls may be repeated and charged again.'
    : est.state === 'incomplete' ? 'Continue scanning remaining tiles.' : 'Start scanning.';
  return `${est.area}: ${action} This click uses at most ${est.calls} lookups (up to $${est.cost_usd.toFixed(2)} at the configured list rate). ` +
    `${est.queued_tiles ?? 'Unknown number of'} tiles queued; dense tiles can add searches, so total lookups needed to finish are unknown and more runs may be needed. ` +
    ((est.queued_tiles ?? 0) > est.calls ? 'This area cannot finish in this click. ' : '') +
    `Recorded area usage: ${est.recorded_calls == null ? 'unavailable' : `${est.recorded_calls} lookups${est.price_per_call_usd != null ? ` ($${(est.recorded_calls * est.price_per_call_usd).toFixed(2)} at the configured rate)` : ''}`}; ${est.reserved_calls ?? 0} reserved lookups have unresolved outcomes; ${est.failed_calls ?? 0} failed calls returned no usable response. ` +
    (est.cap != null ? `Monthly allowance remaining: ${Math.max(0, est.cap - (est.month_calls ?? 0))}. ` : '') +
    (est.worst_case_calls != null ? `Worst-case remaining plan work: ${est.worst_case_calls} lookups (not a prediction). ` : '') +
    'Places found can include neighbouring areas; their coverage is tracked separately.';
};

/** How many places of an area are left out of its pool: on Square, chains, not a fit, closed. */
export const leftOutCount = (a: AreaStatus): number =>
  a.excluded.square + a.excluded.chain + a.excluded.not_fit + a.excluded.closed + (a.excluded.non_merchant ?? 0);

/** "48 of 49 visited": out of the places that can be visited, the left-out ones not counted. */
export const visitedText = (a: AreaStatus): string => `${a.visited} of ${a.visited + a.remaining} visited`;

/** Why the last lookup stopped, for the card face; "complete" needs no sentence under "Looked up". */
export const scanStopText = (a: AreaStatus): string => {
  const reason = a.stop_reason;
  return reason === 'google_rate_limit' ? "Stopped by Google's per-minute limit; progress is saved. Wait a minute and continue; failed tiles remain queued."
    : reason === 'google_error' ? 'Stopped by a Google error; progress is saved and failed tiles remain queued.'
    : reason === 'interrupted' ? 'Interrupted response; recovery may repeat charged calls.'
    : reason === 'per_click_ceiling' ? 'Stopped at your 300-lookup ceiling; continue remaining tiles.'
    : reason === 'confirmed_ceiling' ? 'Stopped at your confirmed lookup ceiling; continue remaining tiles.'
    : reason === 'monthly_cap' ? 'Stopped at the monthly allowance; continue when budget is available.'
    : '';
};

/** The D114 usage counters, kept in the card's details. */
export const scanUsageText = (a: AreaStatus): string =>
  `Recorded usage: ${a.recorded_calls ?? 'unavailable'} lookups; ${a.reserved_calls ?? 0} unresolved; ${a.failed_calls ?? 0} failed.`;

const doors = (n: number) => `${n} ${n === 1 ? 'door' : 'doors'}`;

/** The card's first line: the scan state (D114) and the public-roster doors still to visit (D115
 *  counts roster rows only). "Looked up · 1 door left to visit". */
export const areaHeadline = (a: AreaStatus): string => {
  const state = scanStateLabel[a.state ?? (a.lookup_complete ? 'complete' : 'legacy-unknown')];
  const inPool = a.visited + a.remaining;
  const left = inPool === 0
    ? a.places === 0 ? 'No doors on the public roster yet' : 'No doors to visit; the rest are left out'
    : a.remaining === 0 ? 'All doors visited' : `${doors(a.remaining)} left to visit`;
  return `${state} · ${left}`;
};

/** What sits behind the card's disclosure: progress, queue, retention (D115) and usage (D114). */
export const areaDetailLines = (a: AreaStatus, now: Date = new Date()): string[] => {
  const held = a.google_places_held ?? 0;
  const expiry = a.google_coordinates_expire_at;
  const lines = [`Public roster: ${doors(a.places)} · ${visitedText(a)}`];
  if (a.state && a.state !== 'new') lines.push(`Tiles queued: ${a.queued_tiles ?? 'unknown'}`);
  if (a.scanned_at) lines.push(`Last looked up ${pacificDateTime(a.scanned_at, now)}`);
  lines.push(
    held === 0
      ? 'Google places held: none.'
      : `Google places held: ${held}, unnamed; earliest coordinate expiry: ${expiry ? pacificDateTime(expiry, now) : 'unavailable (cleanup required)'}.`,
    'Coordinates last at most 29 days. No automatic refresh. Google details cannot be saved for named routing.',
  );
  if (a.stop_reason) lines.push(scanUsageText(a));
  return lines;
};
